import {
  createListenerMiddleware,
  isRejectedWithValue,
  ThunkDispatch,
  TypedStartListening
} from '@reduxjs/toolkit'
import {
  setEnabledWallets,
  setPayrailsStatus,
  setPspInitialized
} from 'modules/common/actions/psp'
import {getHttpRequestId, getPspInitialized} from 'modules/common/selectors'
import {setUserSelectedCurrency} from 'modules/meta/slice'
import logBoFHService, {
  BoFHServiceEndpointNames,
  BoFHServiceStatus
} from 'performance/logBoFHService'
import {AnyAction} from 'redux'
import {payrails} from 'services/payrails'
import {stripe} from 'services/stripe'
import {getCurrentBookingRequestId} from 'utils/bookingRequestId'

import {getDataDogRumInstance} from '@vio/core/src/analytics/components/DataDogClient'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {paymentSettingsApi} from '@vio/core/src/offer/services/paymentSettingsApi'
import {PaymentMethodIntegration} from '@vio/core/src/offer/types/paymentSettings'

import {
  getEligiblePaymentMethodTypes,
  getIsPaymentSettingsLoading,
  getPreselectedPaymentMethod
} from './selectors'
import {createPayrailsSession, resolveEnabledWallets} from './thunks'

import type {CheckoutThunkExtra, RootState} from 'store'

export const paymentSettingsListener = createListenerMiddleware()

type AppDispatch = ThunkDispatch<RootState, CheckoutThunkExtra, AnyAction>
type AppStartListening = TypedStartListening<RootState, AppDispatch>

const startListening =
  paymentSettingsListener.startListening as AppStartListening

startListening({
  matcher: paymentSettingsApi.endpoints.getPaymentSettings.matchFulfilled,
  effect: async (action, listenerApi) => {
    const apiKey = action.payload.pspPublishableKey

    if (apiKey) {
      // stripe.setup() catches errors internally and always resolves,
      // so the rest runs even if Stripe fails to load.
      await stripe.setup(apiKey)
      listenerApi.dispatch(setPspInitialized(true))
      listenerApi.dispatch(
        setEnabledWallets(
          await listenerApi.dispatch(resolveEnabledWallets()).unwrap()
        )
      )
    }
    const state = listenerApi.getState()
    getDataDogRumInstance().addTiming('apmLoaded')
    listenerApi.dispatch(
      trackEvent({
        category: Category.System,
        entity: Entity.PaymentMethods,
        action: Action.Loaded,
        payload: {
          paymentMethodsEligible: getEligiblePaymentMethodTypes(state),
          paymentMethodPreselected: getPreselectedPaymentMethod(state)
        }
      })
    )
  }
})

startListening({
  matcher: paymentSettingsApi.endpoints.getPaymentSettings.matchFulfilled,
  effect: async (action, listenerApi) => {
    if (
      !action.payload.paymentMethods.some(
        ({integration}) => integration === PaymentMethodIntegration.Payrails
      )
    )
      return
    // Offer-check refreshes change `httpRequestId`, which refetches payment
    // settings. `sessionExpired` renews the live session.
    if (payrails.getPayrailsInstance()) return

    const createSession = async () => {
      const session = await listenerApi
        .dispatch(createPayrailsSession())
        .unwrap()
      return session?.sdkConfig
    }

    const setUpPayrails = async () => {
      const initResponse = await createSession()
      if (!initResponse) return false

      return payrails.setup({initResponse, onSessionExpired: createSession})
    }

    const isInitialized = await setUpPayrails().catch(() => false)
    listenerApi.dispatch(setPayrailsStatus(isInitialized ? 'ready' : 'failed'))
  }
})

// updates stripe payment request for wallet payments
// Currently this block is unreachable until we introduce
// mid-session currency change (e.g. PICC) for wallets.
startListening({
  actionCreator: setUserSelectedCurrency,
  effect: async (_action, listenerApi) => {
    const state = listenerApi.getState()
    if (!getPspInitialized(state) || getIsPaymentSettingsLoading(state)) return
    listenerApi.cancelActiveListeners()

    const wallets = await listenerApi.pause(
      listenerApi.dispatch(resolveEnabledWallets()).unwrap()
    )
    listenerApi.dispatch(setEnabledWallets(wallets))
  }
})

startListening({
  matcher: paymentSettingsApi.endpoints.getPaymentSettings.matchRejected,
  effect: (action, listenerApi) => {
    if (!isRejectedWithValue(action)) return

    logBoFHService(
      BoFHServiceEndpointNames.PaymentSettings,
      BoFHServiceStatus.error,
      {
        error: action.payload,
        request_id: getHttpRequestId(listenerApi.getState()),
        booking_request_id: getCurrentBookingRequestId()
      },
      'error'
    )
  }
})

startListening({
  matcher: paymentSettingsApi.endpoints.createPaymentSession.matchRejected,
  effect: (action, listenerApi) => {
    if (!isRejectedWithValue(action)) return

    logBoFHService(
      BoFHServiceEndpointNames.PaymentSession,
      BoFHServiceStatus.error,
      {
        error: action.payload,
        request_id: getHttpRequestId(listenerApi.getState()),
        booking_request_id: getCurrentBookingRequestId()
      },
      'error'
    )
  }
})
